import React, { useState, useMemo } from 'react';
import { toast } from 'components/Toast';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getEmployees, updateEmployee } from 'services/employees';
import { getContractors, updateContractor } from 'services/contractors';
import axiosInstance from '../../axios/axiosInstance';
import { getEmployeeBenefits, getCompanyBenefits } from 'services/benefits';
import { getDepartments } from 'services/departments';
import { getWorkplaces } from 'services/workplaces';
import { useNavigate } from 'react-router-dom';
import routes from 'constants/routes';
import { stringMerger } from 'utils';
import { createNotification } from 'services/timeOff';

const cid = () => localStorage.getItem('companyId');

// ─── colour palette matching the app ─────────────────────────────────────────
const ORANGE   = '#1B75F0';
const ORANGE_L = '#EFF6FF';
const BORDER   = '#e5e7eb';
const TEXT      = '#1a1a2e';
const MUTED     = '#6b7280';

// ─── helpers ─────────────────────────────────────────────────────────────────
const fmt = (v, fallback = '—') => (v !== null && v !== undefined && v !== '') ? v : fallback;
const fmtMoney = (v) => v != null ? `$${parseFloat(v).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}` : '—';
const capitalize = (s) => s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : '—';

const BENEFIT_LABELS = {
    medical: 'Medical', dental: 'Dental', vision: 'Vision',
    retirement: '401k', hsa: 'HSA', fsa: 'FSA',
};

function getBenefitLabel(type = '') {
    return BENEFIT_LABELS[type?.toLowerCase()] ?? capitalize(type);
}

// ─── ConfirmModal ─────────────────────────────────────────────────────────────
function ConfirmModal({ title, message, confirmLabel, confirmColor = '#c62828', onConfirm, onClose, loading }) {
    return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
            <div style={{ background: '#fff', borderRadius: 16, padding: '32px 28px', maxWidth: 420, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.18)' }}>
                <h5 style={{ fontWeight: 700, fontSize: 17, color: TEXT, marginBottom: 10 }}>{title}</h5>
                <p style={{ color: MUTED, fontSize: 14, marginBottom: 24 }}>{message}</p>
                <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                    <button onClick={onClose} disabled={loading} style={{ padding: '8px 20px', borderRadius: 8, border: `1.5px solid ${BORDER}`, background: '#fff', color: TEXT, fontSize: 13, fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer' }}>
                        Cancel
                    </button>
                    <button onClick={onConfirm} disabled={loading} style={{ padding: '8px 20px', borderRadius: 8, border: 'none', background: loading ? '#9ca3af' : confirmColor, color: '#fff', fontSize: 13, fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer' }}>
                        {loading ? 'Processing…' : confirmLabel}
                    </button>
                </div>
            </div>
        </div>
    );
}

// ─── Detail row helper ────────────────────────────────────────────────────────
function DRow({ label, value }) {
    return (
        <div style={{ display: 'flex', gap: 8, marginBottom: 6, fontSize: 13 }}>
            <span style={{ color: MUTED, minWidth: 160, flexShrink: 0 }}>{label}</span>
            <span style={{ color: TEXT, fontWeight: 500, wordBreak: 'break-word' }}>{fmt(value)}</span>
        </div>
    );
}

// ─── Section heading ─────────────────────────────────────────────────────────
function Section({ title, children }) {
    return (
        <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: ORANGE, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10, borderBottom: `1.5px solid ${BORDER}`, paddingBottom: 6 }}>{title}</div>
            {children}
        </div>
    );
}

// ─── BenefitsPanel ───────────────────────────────────────────────────────────
function BenefitsPanel({ personId, personType, allBenefits, companyBenefitMap }) {
    const navigate = useNavigate();

    const benefits = useMemo(() => {
        if (!allBenefits || personType !== 'employee') return [];
        return allBenefits.filter(b => b.employeeId === personId);
    }, [allBenefits, personId, personType]);

    if (personType !== 'employee') {
        return <p style={{ color: MUTED, fontSize: 13, margin: 0 }}>Benefit elections are only available for employees.</p>;
    }
    if (!benefits.length) {
        return <p style={{ color: MUTED, fontSize: 13, margin: 0 }}>No benefit elections on file.</p>;
    }

    return (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            {benefits.map(b => {
                const typeName = b.type || companyBenefitMap[b.companyBenefitId] || b.companyBenefitId || 'Benefit';
                const isDisability = typeName === 'STD' || typeName === 'LTD';
                const hasEmployerContrib = b.companyContribution != null || b.companyContributionPercent != null;
                const benefitEditPath = `${routes.BENEFITS.HOME}${routes.BENEFITS.ADD_EDIT_EMPLOYEE_BENEFIT}?id=${b.id}&returnTo=/onboarding`;

                // Employee row label — STD/LTD coverage % is salary replacement, not what the employee pays
                const employeeLabel = isDisability && b.employeeContributionPercent != null
                    ? `Coverage: ${b.employeeContributionPercent}% of salary`
                    : b.employeeContributionPercent != null
                        ? `${b.employeeContributionPercent}%`
                        : b.employeeDeduction != null
                            ? fmtMoney(b.employeeDeduction)
                            : '—';

                // Employer row — show percent for 401k/disability, dollar amount for others
                const employerLabel = hasEmployerContrib
                    ? (b.companyContributionPercent != null
                        ? `${b.companyContributionPercent}% match`
                        : fmtMoney(b.companyContribution))
                    : null;

                return (
                    <div key={b.id} style={{ background: b.pendingReview ? '#EFF6FF' : '#f0fdf4', border: `1.5px solid ${b.pendingReview ? '#fbbf24' : '#86efac'}`, borderRadius: 10, padding: '10px 16px', minWidth: 220, maxWidth: 280 }}>
                        <div style={{ fontWeight: 700, fontSize: 13, color: TEXT, marginBottom: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
                            <span>{getBenefitLabel(typeName)}</span>
                            {b.pendingReview && (
                                <span style={{ fontSize: 10, background: '#fef3c7', color: '#92400e', borderRadius: 6, padding: '2px 7px', fontWeight: 700, whiteSpace: 'nowrap' }}>
                                    Needs Review
                                </span>
                            )}
                        </div>
                        {b.description && <div style={{ fontSize: 11, color: MUTED, marginBottom: 4 }}>{b.description}</div>}
                        {b.period && <div style={{ fontSize: 11, color: MUTED, marginBottom: 4 }}>Period: {capitalize(b.period)}</div>}
                        {!isDisability && (
                            <div style={{ fontSize: 12, color: MUTED, marginBottom: 2 }}>
                                Employee: {employeeLabel}
                            </div>
                        )}
                        {isDisability && b.employeeContributionPercent != null && (
                            <div style={{ fontSize: 12, color: MUTED, marginBottom: 2 }}>{employeeLabel}</div>
                        )}
                        <div style={{ fontSize: 12, color: MUTED, marginBottom: 6 }}>
                            {isDisability
                                ? <>Employer premium: {employerLabel ?? <span style={{ color: '#1B75F0', fontWeight: 600 }}>Not set</span>}</>
                                : <>Employer: {employerLabel ?? <span style={{ color: '#1B75F0', fontWeight: 600 }}>Not set</span>}</>
                            }
                        </div>
                        <span
                            onClick={() => navigate(benefitEditPath)}
                            style={{ fontSize: 11, color: ORANGE, cursor: 'pointer', textDecoration: 'underline', fontWeight: 700 }}
                        >
                            {hasEmployerContrib ? 'Edit →' : 'Set up →'}
                        </span>
                    </div>
                );
            })}
        </div>
    );
}

// ─── PersonCard ───────────────────────────────────────────────────────────────
function PersonCard({ person, personType, allBenefits, companyBenefitMap, deptMap = {}, workplaceMap = {}, onApprove, onReject, approving, rejecting }) {
    const [expanded, setExpanded] = useState(false);
    const navigate = useNavigate();

    const myBenefits = useMemo(() =>
        personType === 'employee' ? (allBenefits || []).filter(b => b.employeeId === person.id) : [],
    [allBenefits, person.id, personType]);
    const missingContribCount = myBenefits.filter(b =>
        b.companyContribution == null && b.companyContributionPercent == null
        && b.type !== 'STD' && b.type !== 'LTD'
    ).length;

    const name = [person.firstName, person.lastName].filter(Boolean).join(' ') || person.email || '—';
    const payLabel = { salary: 'Salary', hourly: 'Hourly Rate', weekly: 'Weekly Rate', biweekly: 'Biweekly Rate', monthly: 'Monthly Rate', daily: 'Daily Rate' }[person.payType?.toLowerCase()] ?? (person.payType ? `${capitalize(person.payType)} Rate` : 'Pay Rate');

    // Resolve department and work location: employees store string names directly;
    // contractors only store IDs so we look up names from the maps.
    const deptDisplay = person.department || person.departmentName
        || (person.departmentId ? deptMap[person.departmentId] : null) || null;
    const workDisplay = person.workLocation || person.workplaceName
        || (person.primaryWorkplaceId ? workplaceMap[person.primaryWorkplaceId] : null) || null;

    // ── Approval readiness ─────────────────────────────────────────────────────
    const missingFields = [];
    if (!person.payRate || parseFloat(person.payRate) <= 0) missingFields.push('Pay Rate');
    if (!person.payType) missingFields.push('Pay Type');
    if (personType === 'employee' && !person.employmentType) missingFields.push('Employment Type');
    if (missingContribCount > 0) missingFields.push(
        `Employer contribution${missingContribCount !== 1 ? 's' : ''} for ${missingContribCount} benefit${missingContribCount !== 1 ? 's' : ''}`
    );
    const canApprove = missingFields.length === 0;

    const editPath = personType === 'employee'
        ? stringMerger(routes.EMPLOYEES.HOME, routes.EMPLOYEES.ADD_EDIT_EMPLOYEE) + `?id=${person.id}&highlight=setup`
        : stringMerger(routes.CONTRACTOR.HOME, routes.CONTRACTOR.ADD_EDIT_CONTRACTOR) + `?id=${person.id}&highlight=setup`;

    const handleEdit = () => {
        const stateKey = personType === 'employee' ? 'employeeData' : 'contractorData';
        navigate(editPath, { state: { [stateKey]: person, fromOnboarding: true } });
    };

    return (
        <div style={{ background: '#fff', border: `1.5px solid ${BORDER}`, borderRadius: 14, marginBottom: 12, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
            {/* Header row */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 20px', cursor: 'pointer' }} onClick={() => setExpanded(e => !e)}>
                {/* Avatar */}
                <div style={{ width: 42, height: 42, borderRadius: '50%', background: ORANGE_L, color: ORANGE, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 16, flexShrink: 0 }}>
                    {(person.firstName?.[0] ?? person.email?.[0] ?? '?').toUpperCase()}
                </div>

                {/* Name + meta */}
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 15, color: TEXT, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        {name}
                        <span style={{ fontSize: 11, fontWeight: 700, background: personType === 'employee' ? '#eff6ff' : '#f0fdf4', color: personType === 'employee' ? '#1d4ed8' : '#166534', borderRadius: 6, padding: '2px 8px', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                            {personType}
                        </span>
                        <span style={{ fontSize: 11, fontWeight: 700, background: '#fef3c7', color: '#92400e', borderRadius: 6, padding: '2px 8px' }}>
                            Pending Review
                        </span>
                    </div>
                    <div style={{ fontSize: 12, color: MUTED, marginTop: 2, display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                        <span>{fmt(person.email)}</span>
                        {person.jobTitle && <span>{person.jobTitle}</span>}
                        {deptDisplay && <span>{deptDisplay}</span>}
                    </div>
                    {myBenefits.length > 0 && (
                        <div style={{ marginTop: 4, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                            <span style={{ fontSize: 11, background: '#eff6ff', color: '#1d4ed8', borderRadius: 6, padding: '2px 8px', fontWeight: 600 }}>
                                🏥 {myBenefits.length} benefit{myBenefits.length !== 1 ? 's' : ''} elected
                            </span>
                            {missingContribCount > 0 && (
                                <span style={{ fontSize: 11, background: '#EFF6FF', color: '#c2410c', borderRadius: 6, padding: '2px 8px', fontWeight: 700 }}>
                                    ⚠ {missingContribCount} employer contribution{missingContribCount !== 1 ? 's' : ''} not set
                                </span>
                            )}
                        </div>
                    )}
                </div>

                {/* Quick pay info */}
                <div style={{ textAlign: 'right', marginRight: 8, flexShrink: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, color: !person.payRate || parseFloat(person.payRate) <= 0 ? '#dc2626' : ORANGE }}>
                        {fmtMoney(person.payRate)}
                    </div>
                    <div style={{ fontSize: 11, color: MUTED }}>{payLabel || '—'}</div>
                </div>

                {/* Actions */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flexShrink: 0, alignItems: 'flex-end' }} onClick={e => e.stopPropagation()}>
                    <div style={{ display: 'flex', gap: 8 }}>
                        <div style={{ position: 'relative' }}>
                            <button
                                onClick={() => canApprove && onApprove(person, personType)}
                                disabled={approving || !canApprove}
                                title={!canApprove ? `Cannot approve — required: ${missingFields.join('; ')}` : ''}
                                style={{
                                    padding: '7px 18px', borderRadius: 8, border: 'none',
                                    background: (!canApprove || approving) ? '#e5e7eb' : '#22c55e',
                                    color: (!canApprove || approving) ? '#9ca3af' : '#fff',
                                    fontSize: 13, fontWeight: 700,
                                    cursor: (!canApprove || approving) ? 'not-allowed' : 'pointer',
                                    transition: 'all 0.15s'
                                }}
                            >
                                {approving ? 'Approving…' : 'Approve'}
                            </button>
                        </div>
                        <button
                            onClick={handleEdit}
                            style={{ padding: '7px 16px', borderRadius: 8, border: `1.5px solid ${BORDER}`, background: '#fff', color: TEXT, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
                        >
                            Edit
                        </button>
                        <button
                            onClick={() => onReject(person, personType)}
                            disabled={rejecting}
                            style={{ padding: '7px 16px', borderRadius: 8, border: '1.5px solid #fca5a5', background: '#fff', color: '#dc2626', fontSize: 13, fontWeight: 600, cursor: rejecting ? 'not-allowed' : 'pointer' }}
                        >
                            {rejecting ? '…' : 'Reject'}
                        </button>
                    </div>
                    {!canApprove && (
                        <div style={{ fontSize: 11, color: '#dc2626', fontWeight: 600, maxWidth: 280, textAlign: 'right' }}>
                            <span>⚠ Required before approval:</span>
                            <ul style={{ margin: '2px 0 0 0', paddingLeft: 16, fontWeight: 500 }}>
                                {missingFields.map((f, i) => <li key={i}>{f}</li>)}
                            </ul>
                        </div>
                    )}
                </div>

                {/* Expand chevron */}
                <span style={{ color: MUTED, fontSize: 18, transition: 'transform 0.2s', transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)', flexShrink: 0 }}>▾</span>
            </div>

            {/* Expanded detail */}
            {expanded && (
                <div style={{ borderTop: `1px solid ${BORDER}`, padding: '20px 24px', background: '#fafafa' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 24 }}>

                        {/* Personal Info */}
                        <Section title="Personal Information">
                            <DRow label="Full Name" value={name} />
                            <DRow label="Email" value={person.email} />
                            <DRow label="Phone" value={person.homePhone || person.cellPhone || person.phone} />
                            {personType === 'employee' && <>
                                <DRow label="SSN (last 4)" value={person.ssn ? `•••-••-${String(person.ssn).slice(-4)}` : null} />
                                <DRow label="Date of Birth" value={person.dateOfBirth ? new Date(person.dateOfBirth).toLocaleDateString() : null} />
                                <DRow label="Address" value={[person.addressLine1, person.city, person.state, person.zip].filter(Boolean).join(', ') || null} />
                            </>}
                        </Section>

                        {/* Employment Info */}
                        <Section title="Employment Details">
                            <DRow label={personType === 'contractor' ? 'Service / Work Type' : 'Job Title'} value={personType === 'contractor' ? (person.serviceType || person.jobTitle) : person.jobTitle} />
                            <DRow label="Department" value={deptDisplay} />
                            <DRow label="Work Location" value={workDisplay} />
                            {personType === 'employee' && <DRow label="Employment Type" value={capitalize(person.employmentType)} />}
                            <DRow label="Pay Type" value={capitalize(person.payType)} />
                            <DRow label="Pay Rate" value={fmtMoney(person.payRate)} />
                            {person.startDate && <DRow label="Start Date" value={new Date(person.startDate).toLocaleDateString()} />}
                            {personType === 'contractor' && <DRow label="Business Name" value={person.businessName} />}
                        </Section>

                        {/* Tax Info (employees only) */}
                        {personType === 'employee' && (
                            <Section title="Tax Information">
                                <DRow label="Federal Filing Status" value={capitalize(person.federalFilingStatus)} />
                                <DRow label="Federal Exempt" value={person.federalExempt ? 'Yes' : 'No'} />
                                <DRow label="Extra Withholding" value={person.federalExtraWithholding != null && person.federalExtraWithholding !== 0 ? fmtMoney(person.federalExtraWithholding) : null} />
                                <DRow label="State Filing Status" value={capitalize(person.stateFilingStatus)} />
                                <DRow label="Resident State" value={person.residentState} />
                                <DRow label="Work State" value={person.workState} />
                            </Section>
                        )}
                    </div>

                    {/* Benefits */}
                    {personType === 'employee' && (
                        <Section title="Benefit Elections">
                            <BenefitsPanel personId={person.id} personType={personType} allBenefits={allBenefits} companyBenefitMap={companyBenefitMap} />
                        </Section>
                    )}
                </div>
            )}
        </div>
    );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
const ONBOARDING_API = 'https://onboarding-api.payrollnoot.com';

function fmtInviteDate(iso) {
    if (!iso) return '—';
    try {
        return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch { return '—'; }
}

export default function PendingOnboardings() {
    const qc = useQueryClient();
    const [tab, setTab] = useState('approvals');
    const [confirm, setConfirm] = useState(null);
    const [approvingId, setApprovingId] = useState(null);
    const [rejectingId, setRejectingId] = useState(null);
    const [search, setSearch] = useState('');
    const [filter, setFilter] = useState('all');
    const [inviteActionId, setInviteActionId] = useState(null);
    const [copiedToken, setCopiedToken] = useState(null);

    const { data: employees = [], isLoading: loadingEmp } = useQuery({ queryKey: ['employees'], queryFn: getEmployees });
    const { data: contractors = [], isLoading: loadingCon } = useQuery({ queryKey: ['contractors'], queryFn: getContractors });
    const { data: allBenefits = [] } = useQuery({ queryKey: ['employeeBenefits'], queryFn: getEmployeeBenefits });
    const { data: companyBenefitsRaw = [] } = useQuery({ queryKey: ['companyBenefits'], queryFn: getCompanyBenefits });
    const { data: departmentsList = [] } = useQuery({ queryKey: ['departments'], queryFn: getDepartments });
    const { data: workplacesList = [] } = useQuery({ queryKey: ['workplaces'], queryFn: getWorkplaces });

    const { data: pendingAccounts = [] } = useQuery({
        queryKey: ['pending-approvals', cid()],
        queryFn: async () => (await axiosInstance.get(`/auth/pending-approvals/${cid()}`)).data ?? [],
        enabled: !!cid(),
    });

    const isOpenInvite = (inv) => {
        if (!inv?.email || !String(inv.email).trim()) return false;
        const s = String(inv.status || inv.inviteStatus || inv.state || '').toLowerCase();
        if (['completed', 'used', 'accepted', 'redeemed', 'revoked', 'cancelled', 'canceled', 'approved'].includes(s)) {
            return false;
        }
        if (inv.completed === true || inv.isCompleted === true || inv.used === true) return false;
        return true;
    };

    const { data: pendingInvitesRaw = [], isLoading: loadingInvites, refetch: refetchInvites } = useQuery({
        queryKey: ['pending-invites', cid()],
        queryFn: async () => {
            const token = localStorage.getItem('authToken');
            const res = await fetch(`${ONBOARDING_API}/api/invites/pending`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) throw new Error('Failed to load pending invites');
            const data = await res.json();
            return Array.isArray(data) ? data : [];
        },
        enabled: !!cid(),
        refetchInterval: 60000,
    });

    // Only truly open invites — completed/used/empty-email rows have no Copy/Resend
    const pendingInvites = useMemo(
        () => pendingInvitesRaw.filter(isOpenInvite),
        [pendingInvitesRaw]
    );

    const companyBenefitMap = useMemo(() => {
        const map = {};
        companyBenefitsRaw.forEach(b => { map[b.id] = b.type || b.description || b.id; });
        return map;
    }, [companyBenefitsRaw]);

    const deptMap = useMemo(() => {
        const map = {};
        departmentsList.forEach(d => { map[d.id] = d.name || d.departmentName || d.id; });
        return map;
    }, [departmentsList]);

    const workplaceMap = useMemo(() => {
        const map = {};
        workplacesList.forEach(w => { map[w.id] = w.name || w.workplaceName || w.locationName || w.id; });
        return map;
    }, [workplacesList]);

    const approveMut = useMutation({
        mutationFn: async ({ person, personType }) => {
            if (personType === 'employee') {
                await updateEmployee({ employeeId: person.id, onboardStatus: 'approved', onboardingStatus: 'approved', active: true });
                await axiosInstance.post(`/companies/${cid()}/employees/${person.id}/approve`);
            } else {
                await updateContractor({ contractorId: person.id, onboardingStatus: 'approved', active: true });
                await axiosInstance.post(`/companies/${cid()}/contractors/${person.id}/approve`);
            }
        },
        onMutate: ({ person }) => setApprovingId(person.id),
        onSettled: () => { setApprovingId(null); setConfirm(null); },
        onSuccess: (_, { person, personType }) => {
            qc.invalidateQueries(['employees']);
            qc.invalidateQueries(['contractors']);
            qc.invalidateQueries(['employeeBenefits']);
            qc.invalidateQueries({ queryKey: ['pending-approvals'] });
            const name = [person.firstName, person.lastName].filter(Boolean).join(' ') || 'You';
            createNotification({
                recipientId: person.id,
                recipientType: personType,
                type: 'onboarding_completed',
                title: 'Onboarding Approved!',
                message: `${name}, your account has been approved. You can now log in to your portal.`,
                link: personType === 'contractor' ? '/contractor-portal' : '/employee',
                companyId: cid(),
            }).catch(() => {});
        },
        onError: (err) => {
            const msg = err?.response?.data?.message || err?.message || 'Unknown error';
            toast(`Approval failed: ${msg}`, 'error');
        },
    });

    const rejectMut = useMutation({
        mutationFn: ({ person, personType }) =>
            personType === 'employee'
                ? updateEmployee({ employeeId: person.id, onboardStatus: 'rejected', onboardingStatus: 'rejected' })
                : updateContractor({ contractorId: person.id, onboardingStatus: 'rejected' }),
        onMutate: ({ person }) => setRejectingId(person.id),
        onSettled: () => { setRejectingId(null); setConfirm(null); },
        onSuccess: (_, { person, personType }) => {
            qc.invalidateQueries(['employees']);
            qc.invalidateQueries(['contractors']);
            qc.invalidateQueries({ queryKey: ['pending-approvals'] });
            createNotification({
                recipientId: person.id,
                recipientType: personType,
                type: 'general',
                title: 'Onboarding Not Approved',
                message: 'Your onboarding submission was not approved. Please contact your employer for more information.',
                companyId: cid(),
            }).catch(() => {});
        },
    });

    const pending = useMemo(() => {
        const pendingEmpIds = new Set(pendingAccounts.filter(u => u.role === 'Employee').map(u => u.employeeId).filter(Boolean));
        const pendingConIds = new Set(pendingAccounts.filter(u => u.role === 'Contractor').map(u => u.contractorId).filter(Boolean));
        const empPending = employees.filter(e => pendingEmpIds.has(e.id) && e.onboardingStatus !== 'rejected' && e.onboardStatus !== 'rejected').map(e => ({ ...e, _type: 'employee' }));
        const conPending = contractors.filter(c => pendingConIds.has(c.id) && c.onboardingStatus !== 'rejected' && c.onboardStatus !== 'rejected').map(c => ({ ...c, _type: 'contractor' }));
        const empFallback = employees.filter(e => !pendingEmpIds.has(e.id) && (e.onboardStatus === 'pending_review' || e.onboardingStatus === 'pending_review')).map(e => ({ ...e, _type: 'employee' }));
        const conFallback = contractors.filter(c => !pendingConIds.has(c.id) && (c.onboardStatus === 'pending_review' || c.onboardingStatus === 'pending_review')).map(c => ({ ...c, _type: 'contractor' }));
        return [...empPending, ...conPending, ...empFallback, ...conFallback];
    }, [employees, contractors, pendingAccounts]);

    const filtered = useMemo(() => {
        let list = pending;
        if (filter !== 'all') list = list.filter(p => p._type === filter);
        if (search.trim()) {
            const q = search.toLowerCase();
            list = list.filter(p => [p.firstName, p.lastName, p.email, p.jobTitle, p.department].some(v => v?.toLowerCase().includes(q)));
        }
        return list;
    }, [pending, filter, search]);

    const filteredInvites = useMemo(() => {
        let list = pendingInvites;
        if (filter !== 'all') list = list.filter(i => (i.type || '').toLowerCase() === filter);
        if (search.trim()) {
            const q = search.toLowerCase();
            list = list.filter(i => [i.email, i.jobTitle, i.department, i.type].some(v => v?.toLowerCase().includes(q)));
        }
        return list;
    }, [pendingInvites, filter, search]);

    const loading = loadingEmp || loadingCon;
    const empCount = pending.filter(p => p._type === 'employee').length;
    const conCount = pending.filter(p => p._type === 'contractor').length;
    const inviteEmpCount = pendingInvites.filter(i => i.type === 'employee').length;
    const inviteConCount = pendingInvites.filter(i => i.type === 'contractor').length;

    const handleApprove = (person, personType) => setConfirm({ person, personType, action: 'approve' });
    const handleReject = (person, personType) => setConfirm({ person, personType, action: 'reject' });
    const handleConfirm = () => {
        if (!confirm) return;
        if (confirm.action === 'approve') approveMut.mutate(confirm);
        else rejectMut.mutate(confirm);
    };

    const copyInviteLink = async (inv) => {
        const url = inv.inviteUrl || `https://onboarding.payrollnoot.com/?token=${inv.token}`;
        try {
            await navigator.clipboard.writeText(url);
            setCopiedToken(inv.token);
            setTimeout(() => setCopiedToken(null), 2000);
        } catch {
            toast('Could not copy link', 'error');
        }
    };

    const resendInvite = async (inv) => {
        setInviteActionId(inv.id);
        try {
            const token = localStorage.getItem('authToken');
            const res = await fetch(`${ONBOARDING_API}/api/invites/${inv.id}/resend`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}` },
            });
            const body = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(body.message || 'Resend failed');
            toast(`Invite resent to ${inv.email}`);
            refetchInvites();
        } catch (err) {
            toast(err.message || 'Resend failed', 'error');
        } finally {
            setInviteActionId(null);
        }
    };

    const revokeInvite = async (inv) => {
        if (!window.confirm(`Revoke invite for ${inv.email}? They will no longer be able to use this link.`)) return;
        setInviteActionId(inv.id);
        try {
            const token = localStorage.getItem('authToken');
            const res = await fetch(`${ONBOARDING_API}/api/invites/${inv.id}/revoke`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}` },
            });
            const body = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(body.message || 'Revoke failed');
            toast(`Invite revoked for ${inv.email}`);
            qc.invalidateQueries({ queryKey: ['pending-invites'] });
        } catch (err) {
            toast(err.message || 'Revoke failed', 'error');
        } finally {
            setInviteActionId(null);
        }
    };

    return (
        <div style={{ padding: '28px 32px', maxWidth: 1100, margin: '0 auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
                <div style={{ width: 48, height: 48, borderRadius: 14, background: ORANGE_L, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 }}>👤</div>
                <div>
                    <h2 style={{ margin: 0, fontWeight: 800, fontSize: 22, color: TEXT }}>Onboarding</h2>
                    <p style={{ margin: 0, color: MUTED, fontSize: 14 }}>
                        Approve completed self-onboardings, or track invites that have not been finished yet.
                    </p>
                </div>
            </div>

            <div style={{ display: 'flex', gap: 8, marginBottom: 24, borderBottom: `1.5px solid ${BORDER}` }}>
                {[
                    { id: 'approvals', label: 'Awaiting approval', count: pending.length },
                    { id: 'invites', label: 'Pending invites', count: pendingInvites.length },
                ].map(t => (
                    <button
                        key={t.id}
                        type="button"
                        onClick={() => { setTab(t.id); setSearch(''); setFilter('all'); }}
                        style={{
                            padding: '10px 18px', border: 'none',
                            borderBottom: tab === t.id ? `3px solid ${ORANGE}` : '3px solid transparent',
                            background: 'none', color: tab === t.id ? ORANGE : MUTED, fontWeight: 700, fontSize: 14, cursor: 'pointer', marginBottom: -1.5,
                        }}
                    >
                        {t.label}
                        {t.count > 0 && (
                            <span style={{ marginLeft: 8, background: tab === t.id ? ORANGE : '#e5e7eb', color: tab === t.id ? '#fff' : MUTED, borderRadius: 12, padding: '1px 8px', fontSize: 12 }}>{t.count}</span>
                        )}
                    </button>
                ))}
            </div>

            {tab === 'approvals' && (
                <>
                    {pending.length > 0 && (
                        <div style={{ display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
                            {[
                                { label: 'Total Pending', value: pending.length, color: ORANGE, bg: ORANGE_L },
                                { label: 'Employees', value: empCount, color: '#1d4ed8', bg: '#eff6ff' },
                                { label: 'Contractors', value: conCount, color: '#166534', bg: '#f0fdf4' },
                            ].map(s => (
                                <div key={s.label} style={{ background: s.bg, border: `1.5px solid ${s.color}22`, borderRadius: 12, padding: '14px 22px', minWidth: 140 }}>
                                    <div style={{ fontWeight: 800, fontSize: 24, color: s.color }}>{s.value}</div>
                                    <div style={{ fontSize: 12, color: MUTED, fontWeight: 600 }}>{s.label}</div>
                                </div>
                            ))}
                        </div>
                    )}
                    <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' }}>
                        <div style={{ position: 'relative', flex: 1, minWidth: 220 }}>
                            <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: MUTED, fontSize: 15 }}>🔍</span>
                            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name, email, title…" style={{ width: '100%', padding: '9px 12px 9px 36px', borderRadius: 9, border: `1.5px solid ${BORDER}`, fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
                        </div>
                        {['all', 'employee', 'contractor'].map(f => (
                            <button key={f} onClick={() => setFilter(f)} style={{ padding: '8px 18px', borderRadius: 8, border: `1.5px solid ${filter === f ? ORANGE : BORDER}`, background: filter === f ? ORANGE : '#fff', color: filter === f ? '#fff' : TEXT, fontSize: 13, fontWeight: 600, cursor: 'pointer', textTransform: 'capitalize' }}>
                                {f === 'all' ? 'All Types' : capitalize(f)}
                            </button>
                        ))}
                    </div>
                    {loading ? (
                        <div style={{ textAlign: 'center', padding: '60px 0', color: MUTED }}><p style={{ fontWeight: 600 }}>Loading pending onboardings…</p></div>
                    ) : filtered.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '80px 0', color: MUTED }}>
                            <div style={{ fontSize: 48, marginBottom: 16 }}>🎉</div>
                            <h4 style={{ fontWeight: 700, color: TEXT, marginBottom: 8 }}>{pending.length === 0 ? 'No Pending Onboardings' : 'No results match your search'}</h4>
                            <p style={{ fontSize: 14 }}>{pending.length === 0 ? 'All onboarded employees and contractors have been reviewed.' : 'Try adjusting your search or filter.'}</p>
                        </div>
                    ) : (
                        filtered.map(person => (
                            <PersonCard key={`${person._type}-${person.id}`} person={person} personType={person._type} allBenefits={allBenefits} companyBenefitMap={companyBenefitMap} deptMap={deptMap} workplaceMap={workplaceMap} onApprove={handleApprove} onReject={handleReject} approving={approvingId === person.id} rejecting={rejectingId === person.id} />
                        ))
                    )}
                </>
            )}

            {tab === 'invites' && (
                <>
                    {pendingInvites.length > 0 && (
                        <div style={{ display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
                            {[
                                { label: 'Open invites', value: pendingInvites.length, color: ORANGE, bg: ORANGE_L },
                                { label: 'Employees', value: inviteEmpCount, color: '#1d4ed8', bg: '#eff6ff' },
                                { label: 'Contractors', value: inviteConCount, color: '#166534', bg: '#f0fdf4' },
                            ].map(s => (
                                <div key={s.label} style={{ background: s.bg, border: `1.5px solid ${s.color}22`, borderRadius: 12, padding: '14px 22px', minWidth: 140 }}>
                                    <div style={{ fontWeight: 800, fontSize: 24, color: s.color }}>{s.value}</div>
                                    <div style={{ fontSize: 12, color: MUTED, fontWeight: 600 }}>{s.label}</div>
                                </div>
                            ))}
                        </div>
                    )}
                    <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' }}>
                        <div style={{ position: 'relative', flex: 1, minWidth: 220 }}>
                            <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: MUTED, fontSize: 15 }}>🔍</span>
                            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by email…" style={{ width: '100%', padding: '9px 12px 9px 36px', borderRadius: 9, border: `1.5px solid ${BORDER}`, fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
                        </div>
                        {['all', 'employee', 'contractor'].map(f => (
                            <button key={f} onClick={() => setFilter(f)} style={{ padding: '8px 18px', borderRadius: 8, border: `1.5px solid ${filter === f ? ORANGE : BORDER}`, background: filter === f ? ORANGE : '#fff', color: filter === f ? '#fff' : TEXT, fontSize: 13, fontWeight: 600, cursor: 'pointer', textTransform: 'capitalize' }}>
                                {f === 'all' ? 'All Types' : capitalize(f)}
                            </button>
                        ))}
                    </div>
                    {loadingInvites ? (
                        <div style={{ textAlign: 'center', padding: '60px 0', color: MUTED }}><p style={{ fontWeight: 600 }}>Loading pending invites…</p></div>
                    ) : filteredInvites.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '80px 0', color: MUTED }}>
                            <div style={{ fontSize: 48, marginBottom: 16 }}>✉️</div>
                            <h4 style={{ fontWeight: 700, color: TEXT, marginBottom: 8 }}>{pendingInvites.length === 0 ? 'No pending invites' : 'No results match your search'}</h4>
                            <p style={{ fontSize: 14, maxWidth: 420, margin: '0 auto' }}>
                                {pendingInvites.length === 0
                                    ? 'Send invites from Employees or Contractors → Send Onboarding Invites. Open invites appear here until they complete or you revoke them.'
                                    : 'Try adjusting your search or filter.'}
                            </p>
                        </div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                            {filteredInvites.map(inv => {
                                const busy = inviteActionId === inv.id;
                                const isCon = (inv.type || '').toLowerCase() === 'contractor';
                                const canManageInvite = isOpenInvite(inv);
                                return (
                                    <div key={inv.id || inv.token} style={{ border: `1.5px solid ${BORDER}`, borderRadius: 14, padding: '16px 18px', background: inv.expired ? '#fff7ed' : '#fff', display: 'flex', flexWrap: 'wrap', gap: 14, alignItems: 'center', justifyContent: 'space-between' }}>
                                        <div style={{ flex: 1, minWidth: 200 }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
                                                <span style={{ fontWeight: 700, fontSize: 15, color: TEXT }}>{inv.email || '—'}</span>
                                                <span style={{ fontSize: 11, fontWeight: 700, borderRadius: 6, padding: '2px 8px', background: isCon ? '#f0fdf4' : '#eff6ff', color: isCon ? '#166534' : '#1d4ed8', textTransform: 'uppercase' }}>{isCon ? 'Contractor' : 'Employee'}</span>
                                                {inv.expired && <span style={{ fontSize: 11, fontWeight: 700, borderRadius: 6, padding: '2px 8px', background: '#ffedd5', color: '#c2410c' }}>Expired</span>}
                                            </div>
                                            <div style={{ fontSize: 12, color: MUTED }}>
                                                Sent {fmtInviteDate(inv.createdAt)} · Expires {fmtInviteDate(inv.expiresAt)}{inv.jobTitle ? ` · ${inv.jobTitle}` : ''}
                                            </div>
                                        </div>
                                        {canManageInvite && (
                                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                                <button type="button" onClick={() => copyInviteLink(inv)} style={{ padding: '7px 12px', borderRadius: 8, border: `1.5px solid ${BORDER}`, background: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer', color: TEXT }}>
                                                    {copiedToken === inv.token ? 'Copied!' : 'Copy link'}
                                                </button>
                                                <button type="button" disabled={busy} onClick={() => resendInvite(inv)} style={{ padding: '7px 12px', borderRadius: 8, border: 'none', background: ORANGE, color: '#fff', fontSize: 12, fontWeight: 600, cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.7 : 1 }}>
                                                    {busy ? '…' : 'Resend'}
                                                </button>
                                                <button type="button" disabled={busy} onClick={() => revokeInvite(inv)} style={{ padding: '7px 12px', borderRadius: 8, border: '1.5px solid #fecaca', background: '#fff', color: '#dc2626', fontSize: 12, fontWeight: 600, cursor: busy ? 'not-allowed' : 'pointer' }}>
                                                    Revoke
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </>
            )}

            {confirm && (
                <ConfirmModal
                    title={confirm.action === 'approve' ? 'Approve Onboarding' : 'Reject Onboarding'}
                    message={confirm.action === 'approve'
                        ? `Approve ${[confirm.person.firstName, confirm.person.lastName].filter(Boolean).join(' ')}? They will become active in payroll immediately.`
                        : `Reject ${[confirm.person.firstName, confirm.person.lastName].filter(Boolean).join(' ')}? Their status will be set to rejected and they will remain excluded from payroll.`}
                    confirmLabel={confirm.action === 'approve' ? 'Yes, Approve' : 'Yes, Reject'}
                    confirmColor={confirm.action === 'approve' ? '#22c55e' : '#dc2626'}
                    onConfirm={handleConfirm}
                    onClose={() => !approvingId && !rejectingId && setConfirm(null)}
                    loading={!!(approvingId || rejectingId)}
                />
            )}
        </div>
    );
}
